import type {
  AuthActionResultDto,
  AuthSessionDto,
  BlogCategory,
  BlogPostDto,
  CertificateListDto,
  CourseRatingSummaryDto,
  CreateInquiryInput,
  DailyChallengesDto,
  InquiryDto,
  ListResponse,
  LoginInput,
  LearnerLearningSummaryDto,
  NpsStatusDto,
  LearningFilterInput,
  LearningLessonDto,
  LearningModuleDetailDto,
  LearningModuleDto,
  LessonProgressInput,
  LessonQuizAttemptInput,
  ProgramDto,
  RegisterInput,
  ResourceDto,
  ResourceFilterInput,
  GameId,
  GameContentDto,
  GameResultDto,
  GameScoreDto,
  GuestProgressInput,
  GuestProgressSyncDto,
  LeaderboardDto,
  LeaderboardPeriod,
  NotificationListDto,
  NotificationPreferencesDto,
  RegisterResultDto,
  TestimonialDto,
  UserDto,
  VocabGrade,
  VocabReviewSummaryDto,
  VocabularyCardDto,
} from '@vuganawe/types';
import { apiFetch } from './client';

const REVALIDATE_SECONDS = 60;

/**
 * Public content getters used by server components.
 * They return `null` when the API is unreachable so pages can render a
 * friendly error state instead of crashing the whole route.
 */

async function safeGet<T>(path: string): Promise<T | null> {
  try {
    return await apiFetch<T>(path, { revalidate: REVALIDATE_SECONDS });
  } catch (error) {
    console.error(`[api] GET ${path} failed:`, error);
    return null;
  }
}

export function getPrograms(): Promise<ListResponse<ProgramDto> | null> {
  return safeGet('/programs');
}

export function getFeaturedPrograms(): Promise<ListResponse<ProgramDto> | null> {
  return safeGet('/programs/featured');
}

export function getProgram(slug: string): Promise<ProgramDto | null> {
  return safeGet(`/programs/${encodeURIComponent(slug)}`);
}

export function getResources(
  filters: ResourceFilterInput = {},
): Promise<ListResponse<ResourceDto> | null> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  const query = params.toString();
  return safeGet(`/resources${query ? `?${query}` : ''}`);
}

export function getResource(slug: string): Promise<ResourceDto | null> {
  return safeGet(`/resources/${encodeURIComponent(slug)}`);
}

/**
 * Fetch a resource as a signed-in learner so ACCOUNT-gated content unlocks.
 * Errors propagate so the caller can fall back to the preview.
 */
export function getResourceWithAuth(slug: string, token: string): Promise<ResourceDto> {
  return apiFetch<ResourceDto>(`/resources/${encodeURIComponent(slug)}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/* ── Learning journey ─────────────────────────────────────────────────── */

function learningQuery(filters: LearningFilterInput = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  return params.toString();
}

export function getLearningModules(
  filters: LearningFilterInput = {},
): Promise<ListResponse<LearningModuleDto> | null> {
  const query = learningQuery(filters);
  return safeGet(`/learning/modules${query ? `?${query}` : ''}`);
}

export function getLearningModule(slug: string): Promise<LearningModuleDetailDto | null> {
  return safeGet(`/learning/modules/${encodeURIComponent(slug)}`);
}

/**
 * Auth'd variants used inside the student dashboard so per-learner progress
 * (completed lessons, percentages) is reflected. Errors propagate so the
 * dashboard can show a loading/error state instead of silent zeros.
 */
export function getLearningModulesWithAuth(
  filters: LearningFilterInput,
  token: string,
): Promise<ListResponse<LearningModuleDto>> {
  const query = learningQuery(filters);
  return apiFetch<ListResponse<LearningModuleDto>>(`/learning/modules${query ? `?${query}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function getLearningModuleWithAuth(
  slug: string,
  token: string,
): Promise<LearningModuleDetailDto> {
  return apiFetch<LearningModuleDetailDto>(`/learning/modules/${encodeURIComponent(slug)}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function getLearningLesson(slug: string): Promise<LearningLessonDto | null> {
  return safeGet(`/learning/lessons/${encodeURIComponent(slug)}`);
}

export function getLearningLessonWithAuth(slug: string, token: string): Promise<LearningLessonDto> {
  return apiFetch<LearningLessonDto>(`/learning/lessons/${encodeURIComponent(slug)}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function getLearningSummary(token: string): Promise<LearnerLearningSummaryDto> {
  return apiFetch<LearnerLearningSummaryDto>('/learning/me/summary', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Today's daily challenges with live progress. */
export function getDailyChallenges(token: string): Promise<DailyChallengesDto> {
  return apiFetch<DailyChallengesDto>('/learning/me/daily-challenges', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Claim the daily-challenge XP bonus (idempotent per day). */
export function claimDailyChallenges(token: string): Promise<DailyChallengesDto> {
  return apiFetch<DailyChallengesDto>('/learning/me/daily-challenges/claim', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

/** When the learner last answered the NPS survey (null = never). */
export function getNpsStatus(token: string): Promise<NpsStatusDto> {
  return apiFetch<NpsStatusDto>('/learning/me/nps', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Submit an NPS answer (0–10 + optional comment). */
export function submitNps(token: string, score: number, comment?: string): Promise<NpsStatusDto> {
  return apiFetch<NpsStatusDto>('/learning/me/nps', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: comment?.trim() ? { score, comment: comment.trim() } : { score },
  });
}

/**
 * Ranked learners by XP. Pass the learner token so the response includes the
 * caller's own row (rank + XP) even when they are outside the top list.
 */
export function getLeaderboard(
  period: LeaderboardPeriod,
  token?: string | null,
): Promise<LeaderboardDto> {
  return apiFetch<LeaderboardDto>(`/learning/leaderboard?period=${period}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    cache: 'no-store',
  });
}

export function getLearnerCertificates(token: string): Promise<CertificateListDto[]> {
  return apiFetch<CertificateListDto[]>('/learning/me/certificates', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function emailLearnerCertificate(
  token: string,
  certificateId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/learning/me/certificates/${certificateId}/email`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function saveCourseRating(
  moduleId: string,
  token: string,
  rating: number,
): Promise<CourseRatingSummaryDto> {
  return apiFetch<CourseRatingSummaryDto>(`/learning/me/modules/${moduleId}/rating`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: { rating },
  });
}

export function getLearnerVocabulary(token: string): Promise<ListResponse<VocabularyCardDto>> {
  return apiFetch<ListResponse<VocabularyCardDto>>('/learning/me/vocabulary', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Signed-in learner's SM-2 review schedule + daily-goal totals. */
export function getVocabReviews(token: string): Promise<VocabReviewSummaryDto> {
  return apiFetch<VocabReviewSummaryDto>('/learning/me/vocab-reviews', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Grade a card (SM-2) on the server; returns the updated card + fresh totals. */
export function gradeVocabReview(
  token: string,
  word: string,
  grade: VocabGrade,
): Promise<VocabReviewSummaryDto> {
  return apiFetch<VocabReviewSummaryDto>('/learning/me/vocab-reviews', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: { word, grade },
  });
}

/** The learner's best score + play count per learning game. */
export function getGameScores(token: string): Promise<GameScoreDto[]> {
  return apiFetch<GameScoreDto[]>('/learning/me/games', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Admin-managed game content (word pairs + sentences). Empty = use static bank. */
export function getGameContent(): Promise<GameContentDto> {
  return apiFetch<GameContentDto>('/learning/game-content');
}

/** Game content personalized to the signed-in learner's own lesson vocabulary. */
export function getMyGameContent(token: string): Promise<GameContentDto> {
  return apiFetch<GameContentDto>('/learning/me/game-content', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

/** Record a game round; server keeps the best score and awards (capped) XP. */
export function submitGameResult(
  token: string,
  game: GameId,
  correct: number,
  total: number,
  score: number,
): Promise<GameResultDto> {
  return apiFetch<GameResultDto>('/learning/me/games', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: { game, correct, total, score },
  });
}

export function syncGuestProgress(
  token: string,
  input: GuestProgressInput,
): Promise<GuestProgressSyncDto> {
  return apiFetch<GuestProgressSyncDto>('/learning/me/guest-progress', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: input,
  });
}

export function saveLessonProgress(
  lessonId: string,
  token: string,
  input: LessonProgressInput,
): Promise<LearningLessonDto> {
  return apiFetch<LearningLessonDto>(`/learning/me/lessons/${lessonId}/progress`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: input,
  });
}

export function saveLessonFavorite(
  lessonId: string,
  token: string,
  favorite: boolean,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/learning/me/lessons/${lessonId}/favorite`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: { favorite },
  });
}

export function saveQuizAttempt(
  lessonId: string,
  token: string,
  input: LessonQuizAttemptInput,
): Promise<LearningLessonDto> {
  return apiFetch<LearningLessonDto>(`/learning/me/lessons/${lessonId}/quiz-attempts`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: input,
  });
}

/* ── Student accounts ──────────────────────────────────────────────────── */

export function registerLearner(input: RegisterInput): Promise<RegisterResultDto> {
  return apiFetch<RegisterResultDto>('/auth/register', { method: 'POST', body: input });
}

export function loginLearner(input: LoginInput): Promise<AuthSessionDto> {
  return apiFetch<AuthSessionDto>('/auth/login', { method: 'POST', body: input });
}

/** Confirm the email-verification link; returns a session (auto sign-in). */
export function verifyEmail(token: string): Promise<AuthSessionDto> {
  return apiFetch<AuthSessionDto>('/auth/verify-email', { method: 'POST', body: { token } });
}

/** Resend the verification email. Always resolves ok (no account enumeration). */
export function resendVerification(email: string): Promise<AuthActionResultDto> {
  return apiFetch<AuthActionResultDto>('/auth/resend-verification', {
    method: 'POST',
    body: { email },
  });
}

/** Request a password-reset email. Always resolves ok (no account enumeration). */
export function forgotPassword(email: string): Promise<AuthActionResultDto> {
  return apiFetch<AuthActionResultDto>('/auth/forgot-password', { method: 'POST', body: { email } });
}

/** Set a new password from a reset link; returns a session (auto sign-in). */
export function resetPassword(token: string, password: string): Promise<AuthSessionDto> {
  return apiFetch<AuthSessionDto>('/auth/reset-password', {
    method: 'POST',
    body: { token, password },
  });
}

/* ── In-app notifications ──────────────────────────────────────────────── */

export function getNotifications(token: string): Promise<NotificationListDto> {
  return apiFetch<NotificationListDto>('/notifications', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function markNotificationRead(token: string, id: string): Promise<NotificationListDto> {
  return apiFetch<NotificationListDto>(`/notifications/${id}/read`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function markAllNotificationsRead(token: string): Promise<NotificationListDto> {
  return apiFetch<NotificationListDto>('/notifications/read-all', {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function getNotificationPreferences(token: string): Promise<NotificationPreferencesDto> {
  return apiFetch<NotificationPreferencesDto>('/notifications/preferences', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function updateNotificationPreferences(
  token: string,
  emailReminders: boolean,
): Promise<NotificationPreferencesDto> {
  return apiFetch<NotificationPreferencesDto>('/notifications/preferences', {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: { emailReminders },
  });
}

/** Sign in (or register) with a Google Identity Services ID token. */
export function loginWithGoogle(credential: string): Promise<AuthSessionDto> {
  return apiFetch<AuthSessionDto>('/auth/google', { method: 'POST', body: { credential } });
}

export function getCurrentUser(token: string): Promise<UserDto> {
  return apiFetch<UserDto>('/auth/me', {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
}

export function getBlogPosts(category?: BlogCategory): Promise<ListResponse<BlogPostDto> | null> {
  const query = category ? `?category=${category}` : '';
  return safeGet(`/blog${query}`);
}

export function getBlogPost(slug: string): Promise<BlogPostDto | null> {
  return safeGet(`/blog/${encodeURIComponent(slug)}`);
}

export function getTestimonials(): Promise<ListResponse<TestimonialDto> | null> {
  return safeGet('/testimonials');
}

/** Client-side form submission — errors propagate so forms can show them. */
export function submitInquiry(input: CreateInquiryInput): Promise<InquiryDto> {
  return apiFetch<InquiryDto>('/inquiries', { method: 'POST', body: input });
}
